import { useState } from 'react';
import { Link } from 'react-router';
import type { FieldMetadata, MergeRecord, ObjectMetadata } from '../../data/client';
import { useMerges } from '../../data/hooks';
import { formatDateTime } from '../../fields/format';
import { Button, GroupedForm, Skeleton } from '../../ui';
import { useSession } from '../auth/useSession';
import { UnmergeDialog } from './UnmergeDialog';

/**
 * THE MERGE HISTORY of one record, and the only place an unmerge starts (4.7).
 *
 * It is on the RECORD rather than in an admin area because that is where somebody
 * notices: a coach opens a customer, sees data that belongs to somebody else, and the
 * answer to "why" is the merge listed right here. The undo is one click from that
 * realisation, and admin-only (question 5, LOCKED).
 *
 * A merged-away record shows its side of the same history, which is what makes the
 * promise on the review card true: the loser is kept and readable, and it says what
 * happened to it.
 *
 * WAVE 3 (R-B1): A GROUP BOX OF ROWS, AND SENTENCES INSTEAD OF CAPSULES. The `Card` is gone
 * for the reason the identity boxes beside it record, and the two `Badge`s - "Undone",
 * "Partly undoable" - are now the end of the row's own sentence. "Partly undoable" was the
 * worst of them: a capsule is a label, and whether an undo would be complete is a
 * consequence, which only a sentence can say.
 * hig: https://developer.apple.com/design/human-interface-guidelines/writing ("Be clear.
 * Choose words that are easily understood and convey the right thing.")
 */
export interface MergeHistoryCardProps {
  readonly object: ObjectMetadata;
  readonly recordId: string;
  readonly fields: readonly FieldMetadata[];
}

export function MergeHistoryCard({ object, recordId, fields }: MergeHistoryCardProps) {
  const session = useSession();
  const isAdmin = session.data?.role === 'admin';
  const merges = useMerges({ objectId: object.id, recordId });
  const [undoing, setUndoing] = useState<MergeRecord | null>(null);

  const entries = merges.data ?? [];
  if (merges.isPending) {
    return (
      <GroupedForm title="Merges">
        <div className="record-row record-row-stacked" aria-busy="true">
          <Skeleton className="h-4 w-48" label="Loading the merge history" />
        </div>
      </GroupedForm>
    );
  }
  // No merges is the normal state and needs no box explaining itself.
  if (merges.isError || entries.length === 0) return null;

  return (
    <GroupedForm title="Merges">
      <ul>
        {entries.map((merge) => {
          const isWinner = merge.winnerRecordId === recordId;
          const otherId = isWinner ? merge.loserRecordId : merge.winnerRecordId;
          const decidedBy = merge.decidedBy.displayName ?? merge.decidedBy.source;
          return (
            <li key={merge.id} className="record-row record-row-stacked">
              <p className="text-sm text-ink">
                {isWinner ? 'Absorbed ' : 'Merged into '}
                <Link
                  to={`/objects/${object.id}/records/${otherId}`}
                  className="font-medium text-accent"
                >
                  another record
                </Link>
                {`, ${decidedBy} on ${formatDateTime(merge.decidedAt)}.`}
              </p>

              {/* THE STATE, AS THE SENTENCE THAT FOLLOWS. Three cases and each of them says
                  what it means for the reader rather than naming itself. */}
              <p className="text-2xs text-ink-muted">
                {merge.undoneAt !== null
                  ? `Undone on ${formatDateTime(merge.undoneAt)}.`
                  : merge.unmergePartial
                    ? 'Undoing this would not restore everything: some of what was written afterwards stays where it is.'
                    : 'This can still be undone.'}
              </p>

              {isAdmin && merge.undoneAt === null && (
                <div>
                  <Button variant="ghost" onClick={() => setUndoing(merge)}>
                    Undo merge
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {undoing !== null && (
        <UnmergeDialog merge={undoing} fields={fields} onClose={() => setUndoing(null)} />
      )}
    </GroupedForm>
  );
}
